import { ExtensionBoilerplate } from '@adornis/buildify/client/extensions/ContentExtensionBoilerplate.js';
import { EditExtensionData } from '@adornis/buildify/client/extensions/EditExtensionData.js';
import type { BuildifyExtension } from '@adornis/buildify/client/globals/types.js';
import type { BaseText } from '@adornis/buildify/db/extensions/text/BaseText.js';
import { WindowSizeController } from '@adornis/chemistry/controllers/window-size-controller.js';
import { css } from '@adornis/chemistry/directives/css.js';
import '@adornis/digitale-helden-shared/client/components/component-icon-button.js';
import { tablet } from '@adornis/digitale-helden-shared/db/enums.js';
import { html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { createRef, ref } from 'lit/directives/ref.js';
import { repeat } from 'lit/directives/repeat.js';
import { BaseSlider } from '../db/BaseSlider.js';

export const MarketingSliderExtension = (textExtension: BuildifyExtension<BaseText>): BuildifyExtension<BaseSlider> => {
  return {
    class: BaseSlider,
    toolbar: {
      icon: 'slider',
      text: {
        default: 'Slider',
      },
    },
    group: 'Marketing',
    render(instance, { isSubelement, dropzoneBefore, dropzoneAfter }) {
      return html`
        <marketing-slider
          .textExtension=${textExtension}
          .content=${instance}
          .defaultValues=${this.defaultValues}
          ?sub=${isSubelement}
          .hasDropzoneBefore=${dropzoneBefore}
          .hasDropzoneAfter=${dropzoneAfter}
        ></marketing-slider>
      `;
    },
    editor: ({ content, contentController, controllerBaseKeyPath, host, globalSettings, extensions }) => {
      return html`
        <d-flex space="md">
          <d-accordeon
            .items=${(content.texts ?? []).map((text, index) => ({
              title: html` <d-text> ${index + 1}. Text </d-text> `,
              text: () => html`
                <d-flex space="sm">
                  ${EditExtensionData({
                    host,
                    content: {
                      value: text,
                      path: [...controllerBaseKeyPath, 'texts', index],
                      controller: contentController,
                      extension: textExtension,
                    },
                    globalSettings,
                    extensions,
                  })}

                  <component-icon-button
                    align-self="end"
                    icon="trash"
                    background=${host.colors.tone.warning}
                    @click=${() => {
                      content.texts!.splice(index, 1);
                      host.requestUpdate();
                    }}
                  ></component-icon-button>
                </d-flex>
              `,
            }))}
          ></d-accordeon>

          <component-icon-button
            align-self="end"
            icon="plus"
            @click=${() => {
              if (!content.texts) content.texts = [];
              // @ts-ignore typing is correct
              content.texts.push(new textExtension.class({}));
              host.requestUpdate();
            }}
          ></component-icon-button>
        </d-flex>
      `;
    },
  };
};

@customElement('marketing-slider')
export class MSlider extends ExtensionBoilerplate<BaseSlider> {
  @property({ attribute: false }) textExtension?: BuildifyExtension;
  @property({ type: Number }) speed = 2; // Geschwindigkeit des Scrolls

  @state() private readonly _size = new WindowSizeController(this);
  @state() private _position = 0;
  @state() private readonly _sliderRef = createRef<HTMLDivElement>();
  @state() private _direction: 'left' | 'right' = 'left';

  private _animationFrameId: number | null = null;
  private _progress = 0; // Fortschritt für die Easing-Kurve
  private _isHovered = false; // Hover-Status

  override connectedCallback(): void {
    super.connectedCallback();

    this._size.observable.subscribe(() => {
      if (this.clientWidth <= tablet) {
        this._isHovered = true;
        this.startScrolling();
        return;
      }

      this._isHovered = false;
      this.addEventListener('mouseenter', this._onMouseEnter);
      this.addEventListener('mouseleave', this._onMouseLeave);
    });
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    this.removeEventListener('mouseenter', this._onMouseEnter);
    this.removeEventListener('mouseleave', this._onMouseLeave);
    if (this._animationFrameId) {
      cancelAnimationFrame(this._animationFrameId);
    }
  }

  private _onMouseEnter = () => {
    this._isHovered = true;
    this.startScrolling();
  };

  private _onMouseLeave = () => {
    this._isHovered = false;
    if (this._animationFrameId) {
      cancelAnimationFrame(this._animationFrameId);
      this._animationFrameId = null;
    }
  };

  private startScrolling() {
    const scroll = () => {
      if (!this._isHovered || !this._sliderRef.value) return;

      const containerWidth = this.clientWidth;
      const contentWidth = this._sliderRef.value.scrollWidth;
      const maxScroll = -(contentWidth - containerWidth);

      // Fortschritt für Easing berechnen (zwischen 0 und 1)
      this._progress = Math.min(1, this._progress + 0.01);
      const easingFactor = 1;
      // 0.5 * (1 - Math.cos(this._progress * Math.PI));
      const dynamicSpeed = this.speed * easingFactor;

      // Bewegung anpassen, aber genau bis zum Rand
      if (this._direction === 'left') {
        this._position = Math.max(maxScroll, this._position - dynamicSpeed);
        if (this._position === maxScroll) {
          this._direction = 'right';
          this._progress = 0;
        }
      } else {
        this._position = Math.min(0, this._position + dynamicSpeed);
        if (this._position === 0) {
          this._direction = 'left';
          this._progress = 0;
        }
      }

      this._sliderRef.value.style.transform = `translateX(${this._position}px)`;
      this._animationFrameId = requestAnimationFrame(scroll);
    };

    if (!this._animationFrameId) {
      this._animationFrameId = requestAnimationFrame(scroll);
    }
  }

  override _content(content: BaseSlider) {
    return html`
      <div ${ref(this._sliderRef)}>
        <d-flex horizontal crossaxis-center padding="lg" space="lg" ${css({ width: 'max-content' })}>
          ${repeat(
            content.texts ?? [],
            text => text._id,
            text => this.textExtension?.render(text, { isSubelement: true }),
          )}
        </d-flex>
      </div>
    `;
  }

  override styles() {
    return [
      ...super.styles(),
      {
        ':host': {
          maxWidth: '100%',
          overflow: 'hidden',
          scrollBehaviour: 'smooth',
          duration: '300ms',
        },
      },
    ];
  }
}
